/**
 * Profile types corrigidos para resolver problemas de persistência do avatar_url
 *
 * PROBLEMAS RESOLVIDOS:
 * 1. Type mismatch entre Supabase types e interfaces locais
 * 2. avatar_url nullable mas opcional causando confusão no TypeScript
 * 3. Strict mode violations removendo campos em compilação
 * 4. Inconsistências entre updateProfile e Profile base
 */

import { Tables } from '@/integrations/supabase/types';

// Type base da tabela profiles (gerado pelo Supabase)
type DatabaseProfile = Tables<'profiles'>;

/**
 * Profile corrigido com avatar_url consistentemente tipado
 *
 * IMPORTANTE: avatar_url é explicitamente opcional (?string | null)
 * para evitar confusão entre "não informado" e "explicitamente null"
 */
export interface SafeProfile {
  id: string;
  avatar_url?: string | null;  // ?string | null = pode não existir OU ser explicitamente null
  company_id?: number | null;
  full_name?: string | null;
  is_admin?: boolean | null;
  updated_at?: string | null;
  username?: string | null;
  website?: string | null;
}

/**
 * Type para atualização de perfil com avatar_url seguro
 *
 * Diferença crítica: updateProfile permite avatar_url: null (remover)
 * mas não remove outros campos acidentalmente
 */
export interface ProfileUpdate {
  full_name?: string | null;
  avatar_url?: string | null;  // Permite explicitamente null para remover avatar
  company_id?: number | null;
  username?: string | null;
  website?: string | null;
}

/**
 * Type para criação de perfil (todos os campos opcionais)
 */
export interface ProfileCreate {
  id: string;
  avatar_url?: string | null;
  company_id?: number | null;
  full_name?: string | null;
  is_admin?: boolean | null;
  username?: string | null;
  website?: string | null;
}

/**
 * Profile com avatar sincronizado (usado em componentes de avatar)
 * Garante que avatar_url seja sempre uma string válida ou null
 */
export interface ProfileWithAvatar extends Omit<SafeProfile, 'avatar_url'> {
  avatar_url: string | null;  // Sempre definido (pode ser null)
}

/**
 * Type guard para validar profile do Supabase
 * Converte DatabaseProfile para SafeProfile de forma segura
 */
export function validateProfile(profile: unknown): SafeProfile | null {
  if (!profile || typeof profile !== 'object') {
    return null;
  }

  const dbProfile = profile as DatabaseProfile;

  return {
    id: dbProfile.id,
    avatar_url: dbProfile.avatar_url,  // Mantém null se existir no DB
    company_id: dbProfile.company_id ?? null,
    full_name: dbProfile.full_name ?? null,
    is_admin: dbProfile.is_admin ?? null,
    updated_at: dbProfile.updated_at ?? null,
    username: dbProfile.username ?? null,
    website: dbProfile.website ?? null,
  };
}

/**
 * Type guard para converter profile para ProfileWithAvatar
 * Garante que avatar_url seja sempre string | null (nunca undefined)
 */
export function normalizeAvatarProfile(profile: SafeProfile | null): ProfileWithAvatar | null {
  if (!profile) {
    return null;
  }

  return {
    ...profile,
    avatar_url: profile.avatar_url ?? null,  // Converte undefined para null
  };
}

/**
 * Type para updateProfile parameters - mais seguro que Partial<Profile>
 * Evita que TypeScript remova avatar_url acidentalmente
 */
export type UpdateProfileParams = ProfileUpdate;

/**
 * Verifica se uma URL de avatar é válida
 * Type guard para evitar problemas com URLs inválidas
 */
export function isValidAvatarUrl(url: unknown): url is string {
  if (typeof url !== 'string') {
    return false;
  }

  // URL vazia ou apenas espaços não é válida
  if (!url.trim()) {
    return false;
  }

  // System avatars são válidas (não são null, mas especiais)
  if (url.includes('system_avatar')) {
    return true;
  }

  // URL deve começar com http ou https
  return url.startsWith('http://') || url.startsWith('https://');
}

/**
 * Type para avatar processado com cache-busting
 */
export interface ProcessedAvatar {
  originalUrl: string | null;
  processedUrl: string | null;
  hasTimestamp: boolean;
  isSystemAvatar: boolean;
}

/**
 * Processa URL de avatar de forma type-safe
 * CRÍTICO: Remove cache-busting da URL para garantir consistência
 * URLs no banco devem ser sempre limpas, sem parâmetros de query
 */
export function processAvatarUrl(url: unknown): ProcessedAvatar {
  if (!isValidAvatarUrl(url)) {
    return {
      originalUrl: null,
      processedUrl: null,
      hasTimestamp: false,
      isSystemAvatar: false,
    };
  }

  const isSystemAvatar = url.includes('system_avatar');
  const hasTimestamp = url.includes('?t=') || url.includes('_cb=') || url.includes('?');
  
  // Remove cache-busting e outros parâmetros de query da URL
  // URLs devem ser sempre limpas no banco de dados
  const cleanUrl = url.split('?')[0];

  return {
    originalUrl: url,
    processedUrl: cleanUrl, // Sempre retorna URL limpa
    hasTimestamp,
    isSystemAvatar,
  };
}

// Export principal para compatibilidade
export type Profile = SafeProfile;

// Re-export para uso em outros módulos
export type {
  SafeProfile as IProfile,
  ProfileUpdate as IProfileUpdate,
  ProfileCreate as IProfileCreate,
  ProfileWithAvatar as IProfileWithAvatar,
  UpdateProfileParams as IUpdateProfileParams,
  ProcessedAvatar as IProcessedAvatar,
};